import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PickerDetails.css';
import OrderDetails from './OrderDetails';
import { getAuthToken } from '../utils/auth';

function PickerDetails({ warehouse, startDate, endDate, country, orderType, excludeCarriers, apiUrl, onBack, onFilterChange }) {
  const API_URL = apiUrl || process.env.REACT_APP_API_URL || 'http://localhost:3001/api/dashboard';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'picks', direction: 'desc' });
  const [availableDates, setAvailableDates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [selectedPicker, setSelectedPicker] = useState(null);

  const getAuthHeaders = () => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchPickerDetails();
  }, [warehouse, startDate, endDate, country, orderType, excludeCarriers]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const headers = getAuthHeaders();
      const [datesRes, countriesRes, orderTypesRes] = await Promise.all([
        axios.get(`${API_URL}/available-dates`, { headers }),
        axios.get(`${API_URL}/countries`, { headers }),
        axios.get(`${API_URL}/order-types`, { headers })
      ]);
      setAvailableDates(datesRes.data.dates || []);
      setCountries(countriesRes.data.countries || []);
      setOrderTypes(orderTypesRes.data.orderTypes || []);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchPickerDetails = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (country && country !== 'All') params.append('country', country);
      if (orderType && orderType !== 'All') params.append('orderType', orderType);
      if (excludeCarriers) params.append('excludeCarriers', 'true');

      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/picker-details/${warehouse}?${params}`, { headers });
      setData(response.data);
    } catch (err) {
      console.error('Error fetching picker details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedPickers = () => {
    if (!data || !data.pickers) return [];

    const sorted = [...data.pickers].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (sortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return sorted;
  };

  const formatNumber = (num) => {
    return num.toLocaleString('en-US');
  };

  const formatCurrency = (num) => {
    return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatWeight = (num) => {
    return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' lbs';
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return ' ⇅';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const orderTypeLabels = {
    'A': 'Assemblies',
    'C': 'Customer',
    'T': 'Transfers'
  };

  if (loading) {
    return <div className="picker-details-loading">Loading picker details...</div>;
  }

  if (!data) {
    return <div className="picker-details-error">Failed to load picker details</div>;
  }

  const sortedPickers = getSortedPickers();

  return (
    <div className="picker-details">
      <div className="picker-header">
        <button className="back-btn" onClick={onBack}>
          ← Back to Summary
        </button>
        <h3>Warehouse: {warehouse}</h3>
        <p className="total-picks">Total Picks: {formatNumber(data.totalPicks)}</p>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              const value = e.target.value;
              onFilterChange({ startDate: value, endDate: value === '' ? '' : endDate });
            }}
          />
        </div>

        <div className="filter-group">
          <label>End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => onFilterChange({ endDate: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <label>Country:</label>
          <select value={country} onChange={(e) => onFilterChange({ country: e.target.value })}>
            <option value="">All</option>
            {countries.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Order Type:</label>
          <select value={orderType} onChange={(e) => onFilterChange({ orderType: e.target.value })}>
            <option value="">All</option>
            {orderTypes.map(type => (
              <option key={type} value={type}>{orderTypeLabels[type] || type}</option>
            ))}
          </select>
        </div>

        <div className="filter-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={excludeCarriers}
              onChange={(e) => onFilterChange({ excludeCarriers: e.target.checked })}
            />
            Exclude Carriers: Z/WC/HFSK
          </label>
        </div>
      </div>

      <div className="picker-table-container">
        <table className="picker-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')}>
                Picker Name{getSortIndicator('name')}
              </th>
              <th onClick={() => handleSort('avg_days_to_pick')}>
                Avg Days to Pick{getSortIndicator('avg_days_to_pick')}
              </th>
              <th onClick={() => handleSort('picks')}>
                Picks{getSortIndicator('picks')}
              </th>
              <th onClick={() => handleSort('lines')}>
                Lines{getSortIndicator('lines')}
              </th>
              <th onClick={() => handleSort('quantity')}>
                Quantity{getSortIndicator('quantity')}
              </th>
              <th onClick={() => handleSort('weight')}>
                Weight{getSortIndicator('weight')}
              </th>
              <th onClick={() => handleSort('dollars')}>
                Dollars{getSortIndicator('dollars')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedPickers.map((picker, index) => (
              <tr key={index}>
                <td className="picker-name clickable" onClick={() => setSelectedPicker(picker.name)}>
                  {picker.name}
                </td>
                <td>{picker.avg_days_to_pick?.toFixed(2) || '0.00'}</td>
                <td>{formatNumber(picker.picks)}</td>
                <td>{formatNumber(picker.lines)}</td>
                <td>{formatNumber(picker.quantity)}</td>
                <td>{formatWeight(picker.weight)}</td>
                <td>{formatCurrency(picker.dollars)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Order Details View */}
      {selectedPicker && (
        <OrderDetails
          warehouse={warehouse}
          picker={selectedPicker}
          startDate={startDate}
          endDate={endDate}
          country={country}
          orderType={orderType}
          excludeCarriers={excludeCarriers}
          apiUrl={API_URL}
          onBack={() => setSelectedPicker(null)}
        />
      )}
    </div>
  );
}

export default PickerDetails;
